import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { 
  Camera, Upload, Loader2, CheckCircle, FileText, X, Sparkles, 
  Plus, Trash2, Eye, AlertCircle, FolderOpen 
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DOCUMENT_CATEGORIES = [
  { value: 'ids_travel', label: 'ID / Travel Document' },
  { value: 'insurance', label: 'Insurance Card' },
  { value: 'car_registration', label: 'Vehicle Document' },
  { value: 'licenses', label: 'License / Certification' },
  { value: 'home', label: 'Home Document' },
  { value: 'work', label: 'Work Document' },
  { value: 'other', label: 'Other' },
];

/**
 * BatchScanDialog - Scan multiple documents in one session
 * 
 * Flow:
 * 1. User adds multiple images
 * 2. Assigns categories to each
 * 3. Processes all with AI
 * 4. Shows results with batch actions
 */
const BatchScanDialog = ({ open, onOpenChange }) => {
  const { sessionToken } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [step, setStep] = useState('collect'); // collect, processing, results
  const [documents, setDocuments] = useState([]); // Array of { id, imageData, preview, category, status, extractedData, docId }
  const [currentProcessing, setCurrentProcessing] = useState(0);
  const [processingComplete, setProcessingComplete] = useState(false);

  const resetDialog = () => {
    setStep('collect');
    setDocuments([]);
    setCurrentProcessing(0);
    setProcessingComplete(false);
  };

  const handleClose = () => {
    resetDialog();
    onOpenChange(false);
  };

  const handleFilesSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const newDocs = [];
    
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image`);
        continue;
      }
      
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} is too large (max 10MB)`);
        continue;
      }

      const base64 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (event) => resolve(event.target.result);
        reader.readAsDataURL(file);
      });

      newDocs.push({
        id: `doc_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        imageData: base64,
        preview: base64,
        category: 'other',
        status: 'pending', // pending, processing, success, error
        extractedData: null,
        docId: null,
        fileName: file.name,
      });
    }

    if (newDocs.length > 0) {
      setDocuments(prev => [...prev, ...newDocs]);
      toast.success(`Added ${newDocs.length} document(s)`);
    }
    
    // Reset input
    e.target.value = '';
  };

  const handleRemoveDocument = (id) => {
    setDocuments(prev => prev.filter(d => d.id !== id));
  };

  const handleCategoryChange = (id, category) => {
    setDocuments(prev => prev.map(d => 
      d.id === id ? { ...d, category } : d
    ));
  };

  const processDocuments = async () => {
    if (documents.length === 0) {
      toast.error('Add at least one document to process');
      return;
    }

    setStep('processing');
    setCurrentProcessing(0);

    for (let i = 0; i < documents.length; i++) {
      const doc = documents[i];
      setCurrentProcessing(i + 1);
      
      // Update status to processing
      setDocuments(prev => prev.map(d => 
        d.id === doc.id ? { ...d, status: 'processing' } : d
      ));

      try {
        // Step 1: Create document with attachment
        const createResponse = await fetch(`${API_URL}/api/documents`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${sessionToken}`,
          },
          body: JSON.stringify({
            title: `Batch Scan ${i + 1}`,
            category_tab: doc.category,
            document_type: 'Batch Scan',
            attachments: [doc.imageData],
          }),
        });

        if (!createResponse.ok) {
          throw new Error('Failed to create document');
        }

        const createdDoc = await createResponse.json();

        // Step 2: Extract information using OCR
        const extractResponse = await fetch(`${API_URL}/api/documents/${createdDoc.id}/extract`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${sessionToken}`,
          },
        });

        if (!extractResponse.ok) {
          throw new Error('Failed to extract document info');
        }

        const extractResult = await extractResponse.json();
        
        // Update document with results
        setDocuments(prev => prev.map(d => 
          d.id === doc.id ? { 
            ...d, 
            status: extractResult.success ? 'success' : 'error',
            extractedData: extractResult.extracted_data,
            docId: createdDoc.id,
          } : d
        ));

      } catch (error) {
        console.error('Batch scan error:', error);
        setDocuments(prev => prev.map(d => 
          d.id === doc.id ? { ...d, status: 'error' } : d
        ));
      }
    }

    setProcessingComplete(true);
    setStep('results');
    toast.success('Batch processing complete!');
  };

  const handleViewAll = () => {
    handleClose();
    navigate('/documents');
  };

  const handleViewDocument = (docId) => {
    handleClose();
    navigate(`/documents/${docId}`);
  };

  const successCount = documents.filter(d => d.status === 'success').length;
  const errorCount = documents.filter(d => d.status === 'error').length;
  const progress = documents.length > 0 ? (currentProcessing / documents.length) * 100 : 0;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderOpen className="h-5 w-5 text-purple-500" />
            Batch Document Scan
          </DialogTitle>
          <DialogDescription>
            {step === 'collect' && 'Add multiple documents to scan at once'}
            {step === 'processing' && `Processing ${currentProcessing} of ${documents.length}...`}
            {step === 'results' && `Processed ${documents.length} documents`}
          </DialogDescription>
        </DialogHeader>

        {/* Step 1: Collect Documents */}
        {step === 'collect' && (
          <div className="flex-1 overflow-auto space-y-4 py-4">
            {/* Add Documents Button */}
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => fileInputRef.current?.click()}
                data-testid="batch-add-files"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Images
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={handleFilesSelect}
              />
            </div>

            {/* Documents List */}
            {documents.length === 0 ? (
              <div className="text-center py-8 border-2 border-dashed border-slate-200 rounded-lg">
                <Upload className="h-10 w-10 mx-auto text-slate-300 mb-2" />
                <p className="text-slate-500">No documents added yet</p>
                <p className="text-sm text-slate-400">Click "Add Images" or drag files here</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[300px] overflow-y-auto">
                {documents.map((doc, index) => (
                  <div 
                    key={doc.id}
                    className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg"
                  >
                    {/* Preview */}
                    <div className="w-16 h-16 rounded-md overflow-hidden flex-shrink-0 border border-slate-200">
                      <img
                        src={doc.preview}
                        alt={`Document ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    
                    {/* Details */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-700 truncate">
                        {doc.fileName || `Document ${index + 1}`}
                      </p>
                      <Select
                        value={doc.category}
                        onValueChange={(value) => handleCategoryChange(doc.id, value)}
                      >
                        <SelectTrigger className="h-8 mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {DOCUMENT_CATEGORIES.map((cat) => (
                            <SelectItem key={cat.value} value={cat.value}>
                              {cat.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Remove Button */}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemoveDocument(doc.id)}
                      className="text-slate-400 hover:text-red-500"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* Process Button */}
            {documents.length > 0 && (
              <Button
                className="w-full bg-purple-600 hover:bg-purple-700"
                onClick={processDocuments}
                data-testid="batch-process-btn"
              >
                <Sparkles className="h-4 w-4 mr-2" />
                Process {documents.length} Document{documents.length !== 1 ? 's' : ''}
              </Button>
            )}
          </div>
        )}

        {/* Step 2: Processing */}
        {step === 'processing' && (
          <div className="py-8 space-y-6">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Processing documents...</span>
                <span className="text-slate-900 font-medium">{currentProcessing} / {documents.length}</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>

            <div className="flex items-center justify-center gap-3">
              <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
              <span className="text-slate-600">AI is extracting information...</span>
            </div>

            {/* Current Document Preview */}
            {documents[currentProcessing - 1] && (
              <div className="flex items-center justify-center">
                <div className="w-24 h-24 rounded-lg overflow-hidden border-2 border-purple-200">
                  <img
                    src={documents[currentProcessing - 1].preview}
                    alt="Processing"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Results */}
        {step === 'results' && (
          <div className="flex-1 overflow-auto space-y-4 py-4">
            {/* Summary */}
            <div className="flex gap-3">
              <div className="flex-1 p-3 bg-emerald-50 rounded-lg border border-emerald-100 text-center">
                <CheckCircle className="h-6 w-6 mx-auto text-emerald-600 mb-1" />
                <p className="text-2xl font-bold text-emerald-700">{successCount}</p>
                <p className="text-xs text-emerald-600">Successful</p>
              </div>
              {errorCount > 0 && (
                <div className="flex-1 p-3 bg-red-50 rounded-lg border border-red-100 text-center">
                  <AlertCircle className="h-6 w-6 mx-auto text-red-600 mb-1" />
                  <p className="text-2xl font-bold text-red-700">{errorCount}</p>
                  <p className="text-xs text-red-600">Failed</p>
                </div>
              )}
            </div>

            {/* Results List */}
            <div className="space-y-2 max-h-[250px] overflow-y-auto">
              {documents.map((doc, index) => (
                <div 
                  key={doc.id}
                  className={cn(
                    "flex items-center gap-3 p-3 rounded-lg",
                    doc.status === 'success' && "bg-emerald-50",
                    doc.status === 'error' && "bg-red-50"
                  )}
                >
                  <div className="w-12 h-12 rounded-md overflow-hidden flex-shrink-0">
                    <img
                      src={doc.preview}
                      alt={`Document ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-700">
                      {doc.extractedData?.full_name || doc.extractedData?.document_type || `Document ${index + 1}`}
                    </p>
                    <p className="text-xs text-slate-500">
                      {DOCUMENT_CATEGORIES.find(c => c.value === doc.category)?.label}
                    </p>
                  </div>
                  
                  {doc.status === 'success' ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleViewDocument(doc.docId)}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </Button>
                  ) : (
                    <AlertCircle className="h-5 w-5 text-red-500" />
                  )}
                </div>
              ))}
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={resetDialog}
                data-testid="batch-scan-more"
              >
                <Plus className="h-4 w-4 mr-2" />
                Scan More
              </Button>
              <Button
                className="flex-1 bg-purple-600 hover:bg-purple-700"
                onClick={handleViewAll}
                data-testid="batch-view-all"
              >
                <FolderOpen className="h-4 w-4 mr-2" />
                View All Documents
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BatchScanDialog;
